import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { format } from 'date-fns';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import { useGetAgentRunTimeline } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { AgentTraceTable, type Span } from 'src/components/agent-trace';
import { Button } from 'src/components/ui/button';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import { TaskRunOutcomes } from 'src/components/agent-trace/TaskRunOutcomes';
import paths from 'src/paths';
import {
  buildRunDetailCardModel,
  defaultRunDetailTab,
  formatRunDuration,
  isRunDetailTab,
  runDidNotStart,
  shouldShowRunOutcome,
  visibleRunDetailTab,
  type RunDetailState,
  type RunDetailTab,
} from '../agentPageRuns';
import { RunStatusPill } from './RunStatusPill';
import { AgentRunScreenPanel } from './AgentRunScreenPanel';

function RunFacts({ run }: { run: AgentRunSummary }) {
  const duration = formatRunDuration(run.duration);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
      <RunStatusPill status={run.status} notStarted={runDidNotStart(run)} />
      <span>{format(new Date(run.startedAt), 'MMM d, yyyy · h:mm a')}</span>
      {duration && <span className="tabular-nums">{duration}</span>}
      {run.model && <span className="truncate">{run.model}</span>}
    </div>
  );
}

/** The prose block under the facts: what the run did, or why there is nothing to say. */
function RunOutcome({ state }: { state: RunDetailState }) {
  switch (state.kind) {
    case 'running':
      return null;
    case 'loading':
      return <Skeleton className="h-12 w-full" />;
    case 'failed':
      return (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
          {state.message}
        </div>
      );
    case 'blocked':
      return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
          <T context="agent page selected run, run blocked because the agent was re-published while it was interrupted">
            This run was interrupted, and the agent was published again before
            it could resume. Its retry started a fresh run on the new version.
          </T>
        </div>
      );
    case 'not-started':
      return <RunNotStarted reason={state.reason} />;
    case 'outcome':
      return (
        <div className="rounded-md border border-border bg-stone-50 px-3 py-2">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
            <T context="agent page selected run, heading over what the run produced">
              Run Results
            </T>
          </div>
          <p className="line-clamp-6 whitespace-pre-wrap text-[12.5px] text-stone-800">
            {state.text}
          </p>
        </div>
      );
    case 'no-summary':
      return (
        <p className="text-[12px] text-muted-foreground">
          <T context="agent page selected run, no summary text but a trace exists">
            This run finished without writing a summary. The steps below are the
            full record.
          </T>
        </p>
      );
    case 'no-work':
      return (
        <p className="text-[12px] text-muted-foreground">
          <T context="agent page selected run, run finished without doing any work">
            This run finished without doing any work — most often because the
            agent checked its queue and found nothing waiting.
          </T>
        </p>
      );
  }
}

/**
 * Why the start gate said no, in the user's terms. A busy resource is the one
 * worth pointing at: the readiness notice above the run list names the holder
 * and offers the release.
 */
function RunNotStarted({ reason }: { reason: string | null }) {
  const className =
    'rounded-md border border-stone-300 bg-stone-100 px-3 py-2 text-[12.5px] text-stone-700';
  switch (reason) {
    case 'resources_unavailable':
      return (
        <div className={className}>
          <T context="agent page selected run: the run did not start because a required resource was leased to another run">
            This run did not start: a resource the agent needs was in use by
            another run. The agent did no work here. The notice above the run
            list shows who holds the resource now.
          </T>
        </div>
      );
    case 'start_conditions_not_met':
      return (
        <div className={className}>
          <T context="agent page selected run: the run did not start because its start conditions were not met">
            This run did not start: the agent&apos;s start conditions were not
            met at the time. The agent did no work here.
          </T>
        </div>
      );
    default:
      return (
        <div className={className}>
          <T context="agent page selected run: the run did not start, generic reason">
            This run did not start, so the agent did no work here.
          </T>
          {reason && (
            <span className="ml-1 font-mono text-[11.5px] text-stone-500">
              ({reason})
            </span>
          )}
        </div>
      );
  }
}

function useRunDetailCardUi(run: AgentRunSummary) {
  const [tab, setTab] = useState<RunDetailTab>(() => defaultRunDetailTab(run));
  const [screenOpen, setScreenOpen] = useState(false);
  const [screenshotFocus, setScreenshotFocus] = useState<{
    messageId: string;
  } | null>(null);
  return {
    tab,
    screenOpen,
    setScreenOpen,
    screenshotFocus,
    showScreenshot: (span: Span) => {
      setScreenshotFocus({ messageId: span.id });
      setScreenOpen(true);
    },
    handleTabChange: (value: string) => {
      if (isRunDetailTab(value)) setTab(value);
    },
  };
}

/**
 * The selected run, explained: facts stay at the top; details, actions, and
 * the full trace share one tabbed pane so the page does not grow with every
 * section.
 */
export function AgentRunDetailCard({
  run,
  agentDefinitionId,
  agentName,
  organizationId,
  onInspectionChange,
}: {
  run: AgentRunSummary;
  agentDefinitionId: string;
  agentName: string;
  organizationId: string | undefined;
  onInspectionChange?: (open: boolean) => void;
}) {
  const {
    data: timeline,
    isLoading,
    isError,
    refetch,
  } = useGetAgentRunTimeline(run.id);
  const ui = useRunDetailCardUi(run);
  const model = buildRunDetailCardModel(run, timeline);

  return (
    // No height cap on the card: capping it while the inspector filled it
    // clipped the inspector's 400px-minimum body at the card edge, leaving the
    // span list and screenshot unreachable. Each tab bounds its own height and
    // the page scrolls.
    <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-white p-3">
      <RunDetailHeader
        run={model.currentRun}
        agentDefinitionId={agentDefinitionId}
      />

      {organizationId && (
        <AgentRunScreenPanel
          run={model.currentRun}
          timeline={timeline}
          organizationId={organizationId}
          open={ui.screenOpen}
          onOpenChange={ui.setScreenOpen}
          focusRequest={ui.screenshotFocus}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
        />
      )}

      <RunDetailTabs
        tab={ui.tab}
        onTabChange={ui.handleTabChange}
        model={model}
        agentRunId={run.id}
        agentName={agentName}
        onInspectionChange={onInspectionChange}
        onSelectScreenshot={organizationId ? ui.showScreenshot : undefined}
      />
    </section>
  );
}

function RunDetailHeader({
  run,
  agentDefinitionId,
}: {
  run: AgentRunSummary;
  agentDefinitionId: string;
}) {
  return (
    <header className="flex shrink-0 items-start justify-between gap-3">
      <div className="min-w-0 space-y-1.5">
        <h2 className="text-sm font-semibold">
          <T context="agent page selected run heading">Run details</T>
        </h2>
        <RunFacts run={run} />
      </div>
      <Button variant="ghost" size="sm" asChild className="shrink-0">
        <Link
          to={paths.authenticated.backgroundAgents.definitionSessionDetail}
          params={{ agentDefinitionId, agentRunId: run.id }}
        >
          <ArrowSquareOutIcon className="mr-1 h-4 w-4" />
          <T context="link from an agent page to the full run detail page">
            Open full run
          </T>
        </Link>
      </Button>
    </header>
  );
}

function RunDetailTabs({
  tab,
  onTabChange,
  model,
  agentRunId,
  agentName,
  onInspectionChange,
  onSelectScreenshot,
}: {
  tab: RunDetailTab;
  onTabChange: (value: string) => void;
  model: ReturnType<typeof buildRunDetailCardModel>;
  agentRunId: string;
  agentName: string;
  onInspectionChange?: (open: boolean) => void;
  onSelectScreenshot?: (span: Span) => void;
}) {
  return (
    <Tabs
      value={visibleRunDetailTab(tab, model.showTrace)}
      onValueChange={onTabChange}
      className="flex flex-col gap-2"
    >
      <TabsList className="shrink-0">
        <TabsTrigger value="details">
          <T context="agent page run details tab">Details</T>
        </TabsTrigger>
        <TabsTrigger value="actions" disabled={!model.showTrace}>
          <T context="agent page run actions tab">Actions</T>
        </TabsTrigger>
        <TabsTrigger value="trace" disabled={!model.showTrace}>
          <T context="agent page run full trace tab">Full Trace</T>
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="details"
        className="max-h-[min(36rem,70vh)] space-y-3 overflow-y-auto"
      >
        {shouldShowRunOutcome(model.outcomes, model.state) && (
          <RunOutcome state={model.state} />
        )}
        <TaskRunOutcomes
          outcomes={model.outcomes}
          runStartedAt={model.currentRun.startedAt}
        />
      </TabsContent>

      {model.showTrace && (
        <>
          <RunInspectorTab
            value="actions"
            active={tab === 'actions'}
            agentRunId={agentRunId}
            agentName={agentName}
            onInspectionChange={onInspectionChange}
            onSelectScreenshot={onSelectScreenshot}
          />
          <RunInspectorTab
            value="trace"
            active={tab === 'trace'}
            agentRunId={agentRunId}
            agentName={agentName}
            onInspectionChange={onInspectionChange}
            onSelectScreenshot={onSelectScreenshot}
          />
        </>
      )}
    </Tabs>
  );
}

/**
 * The inspector sizes its span list and evidence panes from this height, and
 * each pane scrolls inside it. It must be definite: a percentage of the
 * auto-height tab collapses to the inspector's 400px minimum.
 */
const RUN_INSPECTOR_BODY_HEIGHT = 'min(40rem, 70vh)';

function RunInspectorTab({
  value,
  active,
  agentRunId,
  agentName,
  onInspectionChange,
  onSelectScreenshot,
}: {
  value: 'actions' | 'trace';
  active: boolean;
  agentRunId: string;
  agentName: string;
  onInspectionChange?: (open: boolean) => void;
  onSelectScreenshot?: (span: Span) => void;
}) {
  return (
    <TabsContent value={value} className="min-w-0">
      {active && (
        <AgentTraceTable
          agentRunId={agentRunId}
          agentName={agentName}
          compact
          hideTaskOutcomes
          hideViewToggle
          view={value}
          defaultView={value}
          maxHeight={RUN_INSPECTOR_BODY_HEIGHT}
          onInspectionChange={onInspectionChange}
          onSelectScreenshot={onSelectScreenshot}
        />
      )}
    </TabsContent>
  );
}
